import type {
  ApiError,
  ProjectId,
  Role,
  SessionUser,
  Tag,
  TimeEntry,
  UserStatus,
} from '#shared/types'
import type { Project } from '#shared/projects'
import type { PersonTotal, ProjectTotal, ReportSeries, TagTotal } from '#shared/reports'

export interface NewProject {
  name: string
  description: string
  /** Defaults to true server-side; the form always sends it explicitly. */
  allowsTags?: boolean
}

/** What archiving a project would take out of the pickers. */
export interface ProjectUsage {
  entries: number
  minutes: number
}

/** A tag with the number of entries carrying it — the admin list's shape. */
export interface TagWithUsage extends Tag {
  entries: number
}

export interface NewEntry {
  projectId: ProjectId
  /** Zero or more. Every project accepts any tag, or none. */
  tagIds: string[]
  /** YYYY-MM-DD. */
  date: string
  /** Decimal hours in quarter-hour steps; the API stores whole minutes. */
  hours: number
  notes?: string
}

/** A person who can appear in a report, or in the Users page. */
export interface TeamMember {
  id: string
  name: string
  email: string
  role: Role
  status: UserStatus
  /** null means invited but never signed in. */
  lastLoginAt: string | null
  createdAt: string
}

/** Hours per project for a date range — managers only. */
export interface ProjectHoursReport {
  from: string | null
  to: string | null
  /** null when the report covers everyone. */
  userId: string | null
  totalMinutes: number
  totalEntries: number
  /** Distinct people who logged time in the range. */
  people: number
  /** Every project with its fixed colour slot, independent of this filter. */
  projectSeries: ReportSeries[]
  projects: ProjectTotal[]
  /**
   * Hours per tag. NOT a split of `totalMinutes`: an entry with several tags
   * counts in full under each, so these can add up to more. Anything that
   * renders them has to say so.
   */
  tags: TagTotal[]
}

/** One project's hours split by who logged them — managers only. */
export interface PersonHoursReport {
  projectId: string
  projectName: string
  /** null at both ends means all time. */
  from: string | null
  to: string | null
  totalMinutes: number
  totalEntries: number
  people: PersonTotal[]
}

/** One week of logged time, already labelled. Managers only. */
export interface WeekReport {
  from: string
  to: string
  userId: string | null
  /** The seven dates, Monday first. */
  days: string[]
  totalMinutes: number
  entries: WeekEntry[]
}

export interface WeekEntry {
  id: string
  date: string
  minutes: number
  notes: string | null
  /** The grid groups rows by this; the name is for display. */
  projectId: string
  projectName: string
  /** Tag names, already resolved and sorted. Empty for an untagged entry. */
  tags: string[]
  userId: string
  userName: string
}

export class HttpError extends Error {
  constructor(public status: number, public body: ApiError) {
    super(body.message ?? body.error)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: init.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  })

  if (res.status === 204) return undefined as T
  const body = await res.json().catch(() => ({ error: 'bad_response' }))
  if (!res.ok) throw new HttpError(res.status, body as ApiError)
  return body as T
}

export const api = {
  me: () => request<SessionUser>('/me'),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  listProjects: (includeArchived = false) =>
    request<Project[]>(`/projects${includeArchived ? '?includeArchived=true' : ''}`),

  createProject: (input: NewProject) =>
    request<Project>('/projects', { method: 'POST', body: JSON.stringify(input) }),

  updateProject: (
    id: string,
    patch: { name?: string; description?: string; allowsTags?: boolean; archived?: boolean },
  ) => request<Project>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  projectUsage: (id: string) => request<ProjectUsage>(`/projects/${id}/usage`),

  reorderProjects: (ids: string[]) =>
    request<Project[]>('/projects/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),

  /**
   * Every tag, in name order. Tags are global, so there is no project to
   * scope by — one call gets the whole vocabulary.
   *
   * `includeArchived` is what the pages showing historic entries need: a tag
   * archived last month still has to name the time logged under it.
   */
  listTags: (includeArchived = false) =>
    request<Tag[]>(`/tags${includeArchived ? '?includeArchived=true' : ''}`),

  /** The admin list: every tag, archived included, with its entry count. */
  listTagsWithUsage: () =>
    request<TagWithUsage[]>('/tags?includeArchived=true&withUsage=true'),

  /** The whole list, in the order wanted. Managers only. */
  reorderTags: (ids: string[]) =>
    request<Tag[]>('/tags/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),

  createTag: (input: { name: string }) =>
    request<Tag>('/tags', { method: 'POST', body: JSON.stringify(input) }),

  updateTag: (id: string, patch: { name?: string; archived?: boolean }) =>
    request<Tag>(`/tags/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /**
   * The last few distinct things this person logged, newest first — what the
   * Recent Entries card offers to copy.
   */
  listRecentEntries: (limit = 5) =>
    request<TimeEntry[]>(`/entries/recent?limit=${limit}`),

  listEntries: (range?: { from?: string; to?: string }) => {
    const qs = new URLSearchParams(
      Object.entries(range ?? {}).filter(([, v]) => Boolean(v)) as [string, string][],
    ).toString()
    return request<TimeEntry[]>(`/entries${qs ? `?${qs}` : ''}`)
  },
  createEntry: (input: NewEntry) =>
    request<TimeEntry>('/entries', { method: 'POST', body: JSON.stringify(input) }),

  /**
   * Change one's own entry. The date is not editable — the path scopes the
   * write to the day the caller was looking at. The project is: correcting
   * it is the commonest fix there is, and moving the row beats deleting it
   * and losing the note and tags with it.
   *
   * `notes: ''` clears the note and `tagIds: []` clears the tags; an absent
   * field is left alone.
   */
  updateEntry: (
    date: string,
    id: string,
    patch: { projectId?: string; hours?: number; tagIds?: string[]; notes?: string },
  ) =>
    request<TimeEntry>(`/entries/${date}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  listUsers: () => request<TeamMember[]>('/users'),

  /** Add someone who has never signed in. Their row is what lets them in. */
  inviteUser: (input: { email: string; name?: string; role?: Role }) =>
    request<TeamMember>('/users', { method: 'POST', body: JSON.stringify(input) }),

  updateUser: (id: string, patch: { role?: Role; status?: UserStatus }) =>
    request<TeamMember>(`/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** `week` is any date inside the week wanted; the API snaps it to Monday. */
  reportWeek: (input: { week: string; userId?: string }) => {
    const qs = new URLSearchParams({ week: input.week })
    if (input.userId) qs.set('user', input.userId)
    return request<WeekReport>(`/reports/week?${qs.toString()}`)
  },

  /** Omitting the dates means all time. */
  reportByPerson: (input: { projectId: string; from?: string; to?: string }) => {
    const qs = new URLSearchParams({ project: input.projectId })
    if (input.from) qs.set('from', input.from)
    if (input.to) qs.set('to', input.to)
    return request<PersonHoursReport>(`/reports/by-person?${qs.toString()}`)
  },

  /** `userId` omitted or empty means everyone. */
  reportByProject: (range: { from: string; to: string; userId?: string }) => {
    const qs = new URLSearchParams({ from: range.from, to: range.to })
    if (range.userId) qs.set('user', range.userId)
    return request<ProjectHoursReport>(`/reports/by-project?${qs.toString()}`)
  },

  deleteEntry: (date: string, id: string) =>
    request<void>(`/entries/${date}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
}

/** Full page redirect into the Google OAuth flow. */
export function startGoogleLogin(returnTo: string = window.location.pathname) {
  window.location.href = `/api/auth/google/login?returnTo=${encodeURIComponent(returnTo)}`
}
